import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './SalesPages.css';
import { authFetch } from '../utils/authFetch';

const escapeCsvValue = (value) => {
  const stringValue = value === null || value === undefined ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, headers, rows) => {
  const csvContent = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => row.map(escapeCsvValue).join(',')),
  ].join('\n');

  const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function Policies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    carrier: '',
    coverageType: '',
  });
  const [exportOpen, setExportOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const loadPolicies = async () => {
      try {
        setLoading(true);

        const res = await authFetch('/api/policies');

        if (!res.ok) {
          throw new Error('Failed to load policies');
        }

        const data = await res.json();

        if (Array.isArray(data)) {
          setPolicies(data);
        } else {
          console.error('Unexpected policies response:', data);
          setPolicies([]);
        }
      } catch (err) {
        console.error('Error fetching policies:', err);
        setPolicies([]);
      } finally {
        setLoading(false);
      }
    };

    loadPolicies();
  }, []);

  const formatDate = (value) => {
    if (!value) return '—';

    const dateString = String(value).split('T')[0];
    const [year, month, day] = dateString.split('-');

    if (!year || !month || !day) return '—';

    return `${month}-${day}-${year}`;
  };

  const getClientName = (policy) =>
    policy?.name ||
    [policy?.first_name, policy?.last_name].filter(Boolean).join(' ') ||
    policy?.client_name ||
    'Unnamed Client';

  const getStatus = (policy) =>
    policy?.status ||
    policy?.policy_status ||
    policy?.status_label ||
    'Unknown';

  const getCoverageType = (policy) =>
    policy?.coverageTypeName ||
    policy?.coverage_type_name ||
    policy?.coverage_type ||
    '—';

  const getCarrier = (policy) =>
    policy?.carrierName ||
    policy?.carrier_name ||
    '—';

  const getMedicareSupplementPlanType = (policy) =>
    policy?.coverageTypePlanTypeName ||
    policy?.coverage_type_plan_type_name ||
    policy?.coverage_type_plan_type ||
    policy?.planTypeName ||
    policy?.plan_type_name ||
    policy?.planType ||
    policy?.plan_type ||
    policy?.medicare_supplement_plan_type ||
    '';

  const getProduct = (policy) => {
    const coverageType = getCoverageType(policy);

    if (String(coverageType).trim().toLowerCase() === 'medicare supplement') {
      return (
        getMedicareSupplementPlanType(policy) ||
        policy?.productName ||
        policy?.product_name ||
        policy?.plan_name ||
        '—'
      );
    }

    return (
      policy?.productName ||
      policy?.product_name ||
      policy?.plan_name ||
      '—'
    );
  };

  const getPolicyNumber = (policy) =>
    policy?.policy_number ||
    policy?.policyNumber ||
    '—';

  const statusClass = (value) => {
    const normalized = String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/\//g, '_')
      .replace(/-+/g, '_');

    return `sales-chip ${normalized}`;
  };

  const filteredPolicies = useMemo(() => {
    return policies
      .filter((p) => {
        if (!searchTerm) return true;

        const term = searchTerm.toLowerCase();

        return [
          getClientName(p),
          getStatus(p),
          getCoverageType(p),
          getCarrier(p),
          getProduct(p),
          getPolicyNumber(p),
          p?.email,
          p?.phone,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .filter((p) => !filters.status || getStatus(p) === filters.status)
      .filter((p) => !filters.carrier || getCarrier(p) === filters.carrier)
      .filter((p) => !filters.coverageType || getCoverageType(p) === filters.coverageType);
  }, [policies, searchTerm, filters]);

  const uniqueStatuses = Array.from(new Set(policies.map((p) => getStatus(p)).filter(Boolean)));
  const uniqueCarriers = Array.from(new Set(policies.map((p) => getCarrier(p)).filter(Boolean)));
  const uniqueCoverageTypes = Array.from(
    new Set(policies.map((p) => getCoverageType(p)).filter(Boolean))
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleRowClick = (policy) => {
    navigate(`/client-profile/${policy.client_id}?policyId=${policy.id}`);
  };

  const getExportHeaders = () => [
    'Client',
    'Status',
    'Coverage Type',
    'Carrier',
    'Product',
    'Policy Number',
    'Effective Date',
    'Termination Date',
  ];

  const getExportRows = () =>
    filteredPolicies.map((p) => [
      getClientName(p),
      getStatus(p),
      getCoverageType(p),
      getCarrier(p),
      getProduct(p),
      getPolicyNumber(p),
      formatDate(p.effective_date),
      formatDate(p.termination_date),
    ]);

  const exportToCSV = () => {
    downloadCsv('book-of-business.csv', getExportHeaders(), getExportRows());
    setExportOpen(false);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [getExportHeaders()],
      body: getExportRows(),
      startY: 20,
      styles: { fontSize: 8 },
    });

    doc.save('book-of-business.pdf');
    setExportOpen(false);
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Book of Business</h1>
            <p className="sales-subtitle">
              Search and manage your active policy book across clients, carriers, and coverage types.
            </p>
          </div>
        </div>

        <div className="sales-toolbar-card">
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="sales-search-input"
              style={{ maxWidth: '200px' }}
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <select
              name="carrier"
              value={filters.carrier}
              onChange={handleFilterChange}
              className="sales-search-input"
              style={{ maxWidth: '220px' }}
            >
              <option value="">All Carriers</option>
              {uniqueCarriers.map((carrier) => (
                <option key={carrier} value={carrier}>
                  {carrier}
                </option>
              ))}
            </select>

            <select
              name="coverageType"
              value={filters.coverageType}
              onChange={handleFilterChange}
              className="sales-search-input"
              style={{ maxWidth: '240px' }}
            >
              <option value="">All Coverage Types</option>
              {uniqueCoverageTypes.map((coverageType) => (
                <option key={coverageType} value={coverageType}>
                  {coverageType}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search client, policy number, carrier, product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sales-search-input"
              style={{ minWidth: '280px', flex: 1 }}
            />

            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                onClick={() => setExportOpen(!exportOpen)}
                className="sales-secondary-btn"
                type="button"
              >
                Export ▾
              </button>

              {exportOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: '160px',
                    background: '#fff',
                    border: '1px solid rgba(226, 232, 240, 0.95)',
                    borderRadius: '14px',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                    overflow: 'hidden',
                    zIndex: 50,
                  }}
                >
                  <button
                    onClick={exportToCSV}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={exportToPDF}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>All Policies</h2>
              <p>
                {filteredPolicies.length} polic{filteredPolicies.length === 1 ? 'y' : 'ies'}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Coverage Type</th>
                  <th>Carrier</th>
                  <th>Product</th>
                  <th>Policy Number</th>
                  <th>Effective Date</th>
                  <th>Termination Date</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="sales-empty-cell">
                      Loading book of business...
                    </td>
                  </tr>
                ) : filteredPolicies.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="sales-empty-cell">
                      No policies found.
                    </td>
                  </tr>
                ) : (
                  filteredPolicies.map((policy) => (
                    <tr
                      key={policy.id}
                      className="sales-click-row"
                      onClick={() => handleRowClick(policy)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{getClientName(policy)}</div>
                          <div className="sales-secondary">
                            Client ID: {policy.client_id || '—'}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className={statusClass(getStatus(policy))}>
                          {getStatus(policy)}
                        </span>
                      </td>

                      <td>{getCoverageType(policy)}</td>
                      <td>{getCarrier(policy)}</td>
                      <td>{getProduct(policy)}</td>
                      <td>{getPolicyNumber(policy)}</td>
                      <td>{formatDate(policy.effective_date)}</td>
                      <td>{formatDate(policy.termination_date)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Policies;